import React from 'react';
import { cartLabels } from '@/data/content';

interface GroupSelection {
    period: string;
    fulfilment: string;
    time: string;
}

interface CheckoutPeriodOption {
    key: string;
    fulfilments: string[];
    timeSlots: string[];
}

interface CheckoutGroupSelectorProps {
    date: string;
    selection: GroupSelection;
    availablePeriods: CheckoutPeriodOption[];
    onSelectionChange: (key: string, sel: Partial<GroupSelection>) => void;
    groupKey: string;
}

const periodLabel = (period: string): string => {
    if (period === 'lunch') return cartLabels.period_lunch_label;
    if (period === 'dinner') return cartLabels.period_dinner_label;
    return period;
};

const fulfilmentLabel = (mode: string): string => {
    if (mode === 'pickup') return cartLabels.fulfilment_pickup_label;
    if (mode === 'delivery') return cartLabels.fulfilment_delivery_label;
    return mode;
};

const CheckoutGroupSelector: React.FC<CheckoutGroupSelectorProps> = ({
    date: _date,
    selection,
    availablePeriods,
    onSelectionChange,
    groupKey,
}) => {
    const sel = selection ?? { period: '', fulfilment: '', time: '' };
    const periodConfig = availablePeriods.find((p) => p.key === sel.period);
    const availableFulfilments = periodConfig?.fulfilments ?? [];
    const timeSlots = periodConfig?.timeSlots ?? [];

    const handlePeriodChange = (newPeriod: string) => {
        const newPeriodConfig = availablePeriods.find((p) => p.key === newPeriod);
        const newFulfilments = newPeriodConfig?.fulfilments ?? [];
        const newFulfilment = newFulfilments.includes('pickup') ? 'pickup' : (newFulfilments[0] ?? '');
        onSelectionChange(groupKey, { period: newPeriod, fulfilment: newFulfilment, time: '' });
    };

    return (
        <div className="mt-3 px-3 space-y-2">
            {availablePeriods.length > 0 && (
                <div>
                    <label className="block text-xs font-semibold text-app-muted mb-1">
                        1. {cartLabels.period_label}
                    </label>
                    <select
                        aria-label={cartLabels.period_label}
                        value={sel.period}
                        onChange={(e) => handlePeriodChange(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-app-border bg-app-bg text-app-text text-sm focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all"
                    >
                        {availablePeriods.map((p) => (
                            <option key={p.key} value={p.key}>
                                {periodLabel(p.key)}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {availableFulfilments.length > 0 && (
                <div>
                    <label className="block text-xs font-semibold text-app-muted mb-1">
                        2. {cartLabels.fulfilment_label}
                    </label>
                    <select
                        aria-label={cartLabels.fulfilment_label}
                        value={sel.fulfilment}
                        onChange={(e) => onSelectionChange(groupKey, { fulfilment: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-app-border bg-app-bg text-app-text text-sm focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all"
                    >
                        {availableFulfilments.map((f) => (
                            <option key={f} value={f}>
                                {fulfilmentLabel(f)}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {timeSlots.length > 0 && (
                <div>
                    <label className="block text-xs font-semibold text-app-muted mb-1">
                        3. {cartLabels.time_label} <span className="text-red-500">*</span>
                    </label>
                    <select
                        aria-label={cartLabels.time_label}
                        value={sel.time}
                        onChange={(e) => onSelectionChange(groupKey, { time: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-app-border bg-app-bg text-app-text text-sm focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all"
                    >
                        <option value="">{cartLabels.time_placeholder}</option>
                        {timeSlots.map((slot) => (
                            <option key={slot} value={slot}>
                                {slot}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

export default CheckoutGroupSelector;
